import { useState, useEffect } from 'react';

/**
 * MultiObjectSelector - Component for configuring multi-object insert operations
 *
 * Features:
 * - Multi-select object dropdown
 * - File assignment per object
 * - Dependency ordering (parent before child)
 * - Visual dependency indicators
 * - Up/Down controls for ordering
 */
function MultiObjectSelector({
  availableObjects = [],
  onObjectsChange,
  initialObjects = []
}) {
  const [objects, setObjects] = useState(initialObjects);
  const [selectedObject, setSelectedObject] = useState('');
  const [selectedFileId, setSelectedFileId] = useState('');

  // Notify parent when objects change
  useEffect(() => {
    if (onObjectsChange) {
      onObjectsChange(objects);
    }
  }, [objects, onObjectsChange]);

  const addObject = () => {
    if (!selectedObject) return;

    // Check if object already added
    if (objects.some(obj => obj.objectName === selectedObject)) {
      alert('This object is already in the list');
      return;
    }

    const newObject = {
      objectName: selectedObject,
      fileId: selectedFileId || '', // Can be set later
      dependsOn: [], // Can be configured later
      order: objects.length // Track visual order
    };

    setObjects([...objects, newObject]);
    setSelectedObject('');
    setSelectedFileId('');
  };

  const removeObject = (index) => {
    const removedObjectName = objects[index].objectName;

    // Remove dependencies on this object from other objects
    const updatedObjects = objects
      .filter((_, i) => i !== index)
      .map(obj => ({
        ...obj,
        dependsOn: obj.dependsOn.filter(dep => dep !== removedObjectName)
      }));

    setObjects(updatedObjects);
  };

  const moveObject = (index, direction) => {
    const newObjects = [...objects];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newObjects.length) return;

    // Swap objects
    [newObjects[index], newObjects[targetIndex]] = [newObjects[targetIndex], newObjects[index]];

    setObjects(newObjects);
  };

  const updateFileId = (index, fileId) => {
    const newObjects = [...objects];
    newObjects[index].fileId = fileId;
    setObjects(newObjects);
  };

  const toggleDependency = (childIndex, parentObjectName) => {
    const newObjects = [...objects];
    const child = newObjects[childIndex];

    if (child.dependsOn.includes(parentObjectName)) {
      // Remove dependency
      child.dependsOn = child.dependsOn.filter(dep => dep !== parentObjectName);
    } else {
      // Add dependency
      child.dependsOn = [...child.dependsOn, parentObjectName];
    }

    setObjects(newObjects);
  };

  const getPotentialParents = (currentObjectName) => {
    // All objects except the current one can be potential parents
    return objects
      .filter(obj => obj.objectName !== currentObjectName)
      .map(obj => obj.objectName);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Multi-Object Operation
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure multiple objects to be processed in a single operation.
          Objects are processed in order, with dependencies handled automatically.
        </p>
      </div>

      {/* Add Object Form */}
      <div className="bg-white border border-gray-300 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Add Object</h4>
        <div className="flex gap-3">
          <select
            value={selectedObject}
            onChange={(e) => setSelectedObject(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">-- Select Object --</option>
            {availableObjects
              .filter(obj => !objects.some(o => o.objectName === obj.name))
              .map((obj) => (
                <option key={obj.name} value={obj.name}>
                  {obj.label || obj.name}
                </option>
              ))}
          </select>

          <button
            onClick={addObject}
            disabled={!selectedObject}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Add Object
          </button>
        </div>
      </div>

      {/* Objects List */}
      {objects.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">
            Objects ({objects.length})
          </h4>

          {objects.map((obj, index) => (
            <div
              key={obj.objectName}
              className="bg-white border border-gray-300 rounded-lg p-4"
            >
              {/* Object Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveObject(index, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveObject(index, 'down')}
                      disabled={index === objects.length - 1}
                      className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  <div>
                    <div className="font-medium text-gray-900">{obj.objectName}</div>
                    <div className="text-xs text-gray-500">Order: #{index + 1}</div>
                  </div>
                </div>

                <button
                  onClick={() => removeObject(index)}
                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  title="Remove object"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              {/* File ID Input */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  File ID
                </label>
                <input
                  type="text"
                  value={obj.fileId}
                  onChange={(e) => updateFileId(index, e.target.value)}
                  placeholder="Enter file ID for this object"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {!obj.fileId && (
                  <p className="text-xs text-orange-600 mt-1">
                    ⚠ File ID is required before submission
                  </p>
                )}
              </div>

              {/* Dependencies */}
              {objects.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dependencies (this object depends on):
                  </label>
                  <div className="space-y-1">
                    {getPotentialParents(obj.objectName).length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No other objects available for dependencies</p>
                    ) : (
                      getPotentialParents(obj.objectName).map((parentName) => (
                        <label
                          key={parentName}
                          className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50 p-2 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={obj.dependsOn.includes(parentName)}
                            onChange={() => toggleDependency(index, parentName)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span>{parentName}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {obj.dependsOn.length > 0 && (
                    <p className="text-xs text-blue-600 mt-2">
                      ℹ This object will be processed after: {obj.dependsOn.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {objects.length === 0 && (
        <div className="text-center py-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="mt-2 text-sm text-gray-600">No objects added yet</p>
          <p className="text-xs text-gray-500">Select an object above to get started</p>
        </div>
      )}

      {/* Validation Summary */}
      {objects.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <h5 className="text-sm font-medium text-blue-900 mb-2">Operation Summary</h5>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• {objects.length} object{objects.length !== 1 ? 's' : ''} will be processed</li>
            <li>• Processing order: {objects.map(obj => obj.objectName).join(' → ')}</li>
            {objects.some(obj => !obj.fileId) && (
              <li className="text-orange-700">
                ⚠ {objects.filter(obj => !obj.fileId).length} object(s) missing file ID
              </li>
            )}
            {objects.some(obj => obj.dependsOn.length > 0) && (
              <li>
                • {objects.filter(obj => obj.dependsOn.length > 0).length} object(s) have dependencies
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default MultiObjectSelector;
